/**
 * "Später beantworten" per wizard step (NC-1382, Spec section 9).
 *
 * The missing half of P1's `openItems` mechanism: everything that evaluates a
 * deferred field shipped with #1279, but nothing in the UI could write one. The
 * consequence was concrete — an operator who cannot answer a MANDATORY field could
 * never reach `complete` and had no way to record why. The validation dialog hit
 * exactly that with `aec` ("die Zuweisung der Named-User an Teilnehmer ist offen").
 *
 * Deliberately no `disabled` prop: the surrounding wizard does not disable its
 * fields for a viewer either — "Speichern" is the single place that gates on
 * `canEdit`. Adding one here would also be untestable, because the MUI test mock
 * drops every prop on `Switch` except `checked` and `onChange`.
 *
 * `inputProps`'s aria-label follows RunFastCleanupPanel. The test mock drops
 * `inputProps`, so under Jest the accessible name comes from the wrapper <label>
 * that FormControlLabel renders — that is what `getByLabelText` resolves, and the
 * precedent's passing tests prove it. The explicit aria-label is for the real DOM
 * and for Playwright, where the mock is not in play.
 */

import React from 'react';
import { Alert, Box, FormControlLabel, Switch } from '@mui/material';
import { WorkloadProfileInput } from '../../../services/types';
import { isStepDeferred, setStepDeferred, stepRequiredPaths } from './profileStepFields';

/** Exported so tests and the page agree on the accessible name. */
export const DEFER_LABEL = 'Später beantworten';

interface ProfileStepDeferControlProps {
  /** Active wizard step index; must match STEP_REQUIRED_PATHS. */
  step: number;
  value: WorkloadProfileInput;
  onChange: (next: WorkloadProfileInput) => void;
}

export default function ProfileStepDeferControl({
  step,
  value,
  onChange,
}: ProfileStepDeferControlProps) {
  // A step index the wizard does not know has no paths to defer. Render nothing
  // rather than an inert switch.
  if (stepRequiredPaths(step).length === 0) return null;

  const deferred = isStepDeferred(value.openItems, step);

  return (
    <Box sx={{ mt: 3 }}>
      <FormControlLabel
        control={
          <Switch
            checked={deferred}
            onChange={(event) => onChange(setStepDeferred(value, step, event.target.checked))}
            inputProps={{ 'aria-label': DEFER_LABEL }}
          />
        }
        label={DEFER_LABEL}
      />
      {deferred && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Die Pflichtfelder dieses Schritts gelten als bewusst offen und blockieren den
          Status „vollständig“ nicht. Sie erscheinen im Steckbrief unter „Bewusst offen“.
        </Alert>
      )}
    </Box>
  );
}
